import React from 'react';
import './index.scss';
import { Link } from 'react-router-dom';
import { ReactComponent as PencilIcon } from '$/assets/icons/penciledit.svg';
import { ReactComponent as NewsEarningsIcon } from '$/assets/icons/NewsEarnings.svg';
import classnames from 'classnames';
import BixToggle from '$/components/global/bixToggle';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import ExportButton from '$/components/exportButton';
import ExportDropDownCompanyAdmin from '$/components/exportDropDownCompany/admin';
import ExportDropDownCompanyClient from '$/components/exportDropDownCompany/client';
import BetaTag from '$/components/global/betaTag';
import { getIsCurrentUserBixbyComplianceAdminState } from '$/store/profile/selectors';
import { getBixbyComplianceFeatureFlagState } from '$/store/release-feature-flags/selectors';
import * as Bixby from '$/core';

export interface IPublishButton {
  enabled: boolean;
  publishText?: string;
  cancelText?: string;
  seeAllText?: string;
  seeAllLink?: string;
  publishEnabled: boolean;
  editEnabled?: boolean;
  userCanPublish?: boolean;
  userCannotPublishMessage?: string;
  onPublishClick?: (ev: React.MouseEvent<HTMLButtonElement>) => void;
  onCancelClick?: (ev: React.MouseEvent<HTMLButtonElement>) => void;
  onEditClick?: (ev: React.MouseEvent<HTMLButtonElement>) => void;
}

export interface ICompanyNewsLink {
  sendCompanyUpdateText?: string;
  sendCompanyUpdateLink?: string | null;
}

export interface IAddButton {
  enabled: boolean;
  text: string;
  onClick: (ev: React.MouseEvent<HTMLButtonElement>) => void;
}

export type IImportButton = IAddButton;

export interface IToggle {
  disabled?: boolean;
  checked: boolean;
  onChange: (ev: boolean) => void;
}

export interface IDropdown {
  enabled: boolean;
  component: JSX.Element;
}

export interface ITag {
  text: string;
  color?: 'red';
  icon?: JSX.Element;
}

interface IHeadingProps {
  title?: string;
  titleElement?: JSX.Element;
  titleOnly?: boolean;
  publishButton?: IPublishButton;
  companyNewsLink?: ICompanyNewsLink;
  addButton?: IAddButton;
  importButton?: IImportButton;
  draftToggle?: IToggle | undefined;
  heatMapToggle?: IToggle | undefined;
  alertsToggle?: IToggle | undefined;
  isEditing?: boolean;
  exportEnabled?: boolean;
  adminCompanyExportEnabled?: boolean;
  clientCompanyExportEnabled?: boolean;
  dropdown?: IDropdown;
  isCompanySECPublic?: boolean;
  declarationType?: Bixby.Enums.DeclarationType;
  includeBetaTag?: boolean;
  children?: React.ReactNode;
  HeadingActionButton?: React.ReactNode | undefined;
  exportButtonLabel?: string;
  tags?: ITag[];
  isBixbyComplianceAdmin: boolean;
  isBixbyComplianceEnabled: boolean;
  handleExport?: () => Promise<void>;
}

const HeadingBlock: React.FC<IHeadingProps> = props => {
  const {
    title,
    titleElement,
    titleOnly,
    publishButton,
    companyNewsLink,
    isEditing,
    addButton,
    importButton,
    draftToggle,
    heatMapToggle,
    alertsToggle,
    exportEnabled,
    adminCompanyExportEnabled,
    clientCompanyExportEnabled,
    dropdown,
    isCompanySECPublic,
    includeBetaTag,
    HeadingActionButton,
    tags,
    declarationType,
    exportButtonLabel,
    isBixbyComplianceAdmin,
    isBixbyComplianceEnabled,
    handleExport = () => Promise.resolve(),
  } = props;

  let headingCss = 'heading_block';
  if (titleOnly) {
    headingCss += ' title_only';
  }

  return (
    <div className={headingCss}>
      <div className={classnames('top_wrap', { add_padding: !tags })}>
        <div className="header_contain">
          <h2 className={classnames('heading_title', { blue_text: isCompanySECPublic })}>
            {titleElement ? titleElement : title} {includeBetaTag ? <BetaTag /> : ''}
          </h2>
          {HeadingActionButton}
        </div>
        <div className="top_options">
          {draftToggle && (
            <BixToggle
              id="draft_toggle"
              disabled={draftToggle.disabled}
              checked={draftToggle.checked}
              text={draftToggle.checked ? 'Live' : 'Draft'}
              onChange={l => draftToggle.onChange(!l)}
            />
          )}
          {alertsToggle && (
            <BixToggle
              id="alerts_toggle"
              checked={alertsToggle.checked}
              text={alertsToggle.checked ? 'Email Alerts Enabled' : 'Email Alerts Disabled'}
              onChange={l => alertsToggle.onChange(!l)}
            />
          )}
          {heatMapToggle && (
            <BixToggle
              id="heat_map_toggle"
              checked={heatMapToggle.checked}
              text={heatMapToggle.checked ? 'Hide Heat Map' : 'Show Heat Map'}
              onChange={l => heatMapToggle.onChange(!l)}
            />
          )}
          {publishButton && (
            <div className={classnames('edit_wrap', { edit_mode: !publishButton.publishEnabled })}>
              {publishButton.publishEnabled && isEditing && (
                <React.Fragment>
                  <button className="edit_btn cancel_btn" onClick={publishButton.onCancelClick}>
                    {publishButton.cancelText || 'Discard Draft'}
                  </button>
                  {publishButton.userCanPublish && (
                    <button className="edit_btn" onClick={publishButton.onPublishClick}>
                      {publishButton.publishText || 'Publish'}
                    </button>
                  )}
                  {!publishButton.userCanPublish && (
                    <button
                      className="edit_btn"
                      disabled
                      title={publishButton.userCannotPublishMessage || 'Publish is disabled for this user.'}>
                      {publishButton.publishText || 'Publish'}
                    </button>
                  )}
                </React.Fragment>
              )}
              {!isEditing && publishButton.editEnabled && (
                <button onClick={publishButton.onEditClick} className="edit_icon_wrap">
                  <PencilIcon />
                </button>
              )}
              {publishButton.seeAllLink && (
                <Link className="see_full" to={publishButton.seeAllLink}>
                  {publishButton.seeAllText || 'See Full List'}
                </Link>
              )}
              {(!isBixbyComplianceEnabled || (isBixbyComplianceEnabled && !isBixbyComplianceAdmin)) &&
                companyNewsLink &&
                companyNewsLink.sendCompanyUpdateLink && (
                  <div className="send_news_wrap">
                    <Link to={companyNewsLink.sendCompanyUpdateLink}>
                      <button
                        className={classnames('send_news_btn', {
                          blue_highlight: isCompanySECPublic,
                          red_highlight: declarationType !== Bixby.Enums.DeclarationType.Public,
                        })}>
                        {companyNewsLink.sendCompanyUpdateText || 'Send News'}
                        <div className="icon_wrap">
                          <NewsEarningsIcon />
                        </div>
                      </button>
                    </Link>
                  </div>
                )}
              {adminCompanyExportEnabled && (
                <div className="export_company_admin">
                  <ExportDropDownCompanyAdmin />
                </div>
              )}
            </div>
          )}

          <div className="actions_wrap">
            {addButton && addButton.enabled && (
              <div className="add_wrap">
                <button type="button" className="add_btn" onClick={addButton.onClick}>
                  + {addButton.text}
                </button>
              </div>
            )}

            {importButton && importButton.enabled && (
              <div className="add_wrap">
                <button type="button" className="import_btn" onClick={importButton.onClick}>
                  + {importButton.text}
                </button>
              </div>
            )}
          </div>

          {dropdown && dropdown.enabled && dropdown.component}
          {exportEnabled && <ExportButton exportButtonLabel={exportButtonLabel} handleExport={handleExport} />}
        </div>
      </div>
      {tags && (
        <div className="tags">
          {tags.map(({ text, color, icon }, ix) => (
            <div className={classnames('tag_contain', { red_tag: color === 'red' })} key={ix}>
              <div className="tag">{text}</div>
              {icon && <div className="tag_icon">{icon}</div>}
            </div>
          ))}
        </div>
      )}
      {props.children ? (
        <div className="heading_tabs">
          {props.children}
          {clientCompanyExportEnabled && (
            <div className="export_company_client">
              <ExportDropDownCompanyClient />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  const { isBixbyComplianceAdmin } = getIsCurrentUserBixbyComplianceAdminState(state);
  const { isBixbyComplianceEnabled } = getBixbyComplianceFeatureFlagState(state);

  return {
    isEditing: state.draftState.isEditing,
    isBixbyComplianceAdmin,
    isBixbyComplianceEnabled,
  };
};

export default connect(mapStateToProps)(HeadingBlock);
